import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { z } from 'zod';
import { Badge, Button, Card, CardBody, CardTitle, Dialog, Field, NotFound } from '../../ui';
import {
  createPlan,
  createWeek,
  duplicateWeek,
  getClient,
  listPlansForClient,
  listWeeksForClient,
  publishWeek,
  unpublishWeek,
} from '../../shared/queries';
import type { WeekRow } from '../../shared/queries';
import { formatDate } from '../../shared/formatDate';
import { supabase } from '../../shared/supabase/client';
import { useSession } from '../session/SessionProvider';
import { Loading } from './AdminLayout';
import { PlanTemplateActions } from './PlanTemplateActions';
import { PublishedWeekNotice } from './publishedWeekNotice';
import { SessionEditor } from './SessionEditor';
import { StatusBadge } from './RosterPage';

/**
 * One client's plan: the tree the coach actually builds.
 *
 * plan → week → session → exercise entry, ad hoc, with no exercise library
 * behind it yet (the library is a later change, and `library_item_id` is
 * already on the row so linking to one later is an UPDATE rather than a
 * migration).
 *
 * Every read here is unfiltered by tenancy and narrowed only by *which of her
 * own clients* she is looking at. `getClient` with another instance's uuid
 * comes back `null`, from the policy, and this page renders the same "Page not
 * found" it renders for a uuid that never existed.
 */
export function ClientPlanPage() {
  const params = useParams();
  const clientId = params['clientId'] ?? '';
  const { profile } = useSession();
  const coachId = profile?.coach_id ?? '';

  const client = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => getClient(supabase, clientId),
  });

  const plans = useQuery({
    queryKey: ['plans', clientId],
    queryFn: () => listPlansForClient(supabase, clientId),
    enabled: client.data != null,
  });

  const [openWeekId, setOpenWeekId] = useState<string | null>(null);

  if (client.isPending) return <Loading />;
  if (!client.data) return <NotFound />;

  const plan = plans.data?.[0] ?? null;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/admin" className="text-ink-muted text-xs underline-offset-2 hover:underline">
          Back to clients
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="text-ink text-2xl font-semibold">{client.data.display_name}</h1>
          <StatusBadge status={client.data.status} />
        </div>
        <p className="text-ink-muted mt-1 text-sm">{client.data.email}</p>
      </div>

      {plans.isPending ? <p className="text-ink-muted text-sm">Loading…</p> : null}

      {!plans.isPending && plan === null ? (
        <NewPlanForm clientId={clientId} coachId={coachId} />
      ) : null}

      {plan === null ? null : (
        <WeeksPanel
          planId={plan.id}
          planTitle={plan.title}
          clientId={clientId}
          clientName={client.data.display_name}
          coachId={coachId}
          openWeekId={openWeekId}
          onOpen={setOpenWeekId}
        />
      )}
    </div>
  );
}

const planSchema = z.object({
  title: z.string().trim().min(1, 'Give the plan a name.').max(120, 'That is too long.'),
});

function NewPlanForm({ clientId, coachId }: { clientId: string; coachId: string }) {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof planSchema>>({
    resolver: zodResolver(planSchema),
    defaultValues: { title: '' },
  });

  const create = useMutation({
    mutationFn: (values: z.infer<typeof planSchema>) =>
      createPlan(supabase, { coach_id: coachId, client_id: clientId, title: values.title }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['plans', clientId] });
    },
  });

  return (
    <Card>
      <CardTitle>Start a plan</CardTitle>
      <CardBody>A plan holds the weeks you build for this client.</CardBody>

      <form
        className="mt-5 flex flex-col gap-4"
        onSubmit={(event) =>
          void handleSubmit((values) => {
            create.mutate(values);
          })(event)
        }
      >
        <Field label="Plan name" error={errors.title?.message} {...register('title')} />
        {create.isError ? (
          <p role="alert" className="text-danger text-sm">
            {create.error.message}
          </p>
        ) : null}
        <div>
          <Button type="submit" disabled={create.isPending}>
            Create plan
          </Button>
        </div>
      </form>
    </Card>
  );
}

const weekSchema = z.object({
  startsOn: z.string().trim(),
});

function WeeksPanel({
  planId,
  planTitle,
  clientId,
  clientName,
  coachId,
  openWeekId,
  onOpen,
}: {
  planId: string;
  planTitle: string;
  clientId: string;
  clientName: string;
  coachId: string;
  openWeekId: string | null;
  onOpen: (id: string | null) => void;
}) {
  const queryClient = useQueryClient();

  const weeks = useQuery({
    queryKey: ['weeks', clientId],
    queryFn: () => listWeeksForClient(supabase, clientId),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<z.infer<typeof weekSchema>>({
    resolver: zodResolver(weekSchema),
    defaultValues: { startsOn: '' },
  });

  /**
   * `<input type="date">` renders in the *browser's* format and the page cannot
   * change that, so a coach on a US-configured machine reads `09/13/2026` in the
   * control and cannot tell from it which half is the month (design D4). The
   * control's value is unambiguous ISO, so nothing is stored wrongly — but the
   * page can still say plainly what was chosen, in the same format and from the
   * same function as the week card she will read a second later. `Field` hangs
   * this off `aria-describedby`, so it is announced with the control rather than
   * being loose text beside it.
   */
  const startsOn = useWatch({ control, name: 'startsOn' });
  const startsOnEcho = /^\d{4}-\d{2}-\d{2}$/.test(startsOn) ? formatDate(startsOn) : undefined;

  const add = useMutation({
    mutationFn: (values: z.infer<typeof weekSchema>) => {
      // The next number, not a number the coach has to keep track of.
      const next = (weeks.data ?? []).reduce((max, week) => Math.max(max, week.week_number), 0) + 1;
      return createWeek(supabase, {
        coach_id: coachId,
        client_id: clientId,
        plan_id: planId,
        week_number: next,
        starts_on: values.startsOn === '' ? null : values.startsOn,
      });
    },
    onSuccess: async (week) => {
      reset();
      onOpen(week.id);
      await queryClient.invalidateQueries({ queryKey: ['weeks', clientId] });
    },
  });

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-ink text-lg font-medium">{planTitle}</h2>

      {/*
        Both template gestures live on the plan, because both are about this
        plan: one keeps what she built here, the other starts what she is about
        to build here. Neither leaves anything behind that points at a template.
      */}
      <PlanTemplateActions
        planId={planId}
        planTitle={planTitle}
        clientId={clientId}
        clientName={clientName}
      />

      <Card>
        <CardTitle>Add a week</CardTitle>
        <CardBody>
          A new week is a draft. Your client sees nothing of it until you publish it.
        </CardBody>
        <form
          className="mt-4 flex flex-wrap items-end gap-3"
          onSubmit={(event) =>
            void handleSubmit((values) => {
              add.mutate(values);
            })(event)
          }
        >
          <Field
            label="Starts on (optional)"
            type="date"
            {...(startsOnEcho === undefined ? {} : { hint: startsOnEcho })}
            error={errors.startsOn?.message}
            {...register('startsOn')}
          />
          <Button type="submit" disabled={add.isPending}>
            Add week
          </Button>
        </form>
        {add.isError ? (
          <p role="alert" className="text-danger mt-3 text-sm">
            {add.error.message}
          </p>
        ) : null}
      </Card>

      {weeks.data?.length === 0 ? (
        <Card>
          <CardBody>No weeks yet.</CardBody>
        </Card>
      ) : null}

      <ul className="flex flex-col gap-4">
        {(weeks.data ?? []).map((week) => (
          <li key={week.id}>
            <WeekCard
              week={week}
              clientId={clientId}
              clientName={clientName}
              coachId={coachId}
              open={openWeekId === week.id}
              onToggle={() => {
                onOpen(openWeekId === week.id ? null : week.id);
              }}
              onDuplicated={onOpen}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function WeekCard({
  week,
  clientId,
  clientName,
  coachId,
  open,
  onToggle,
  onDuplicated,
}: {
  week: WeekRow;
  clientId: string;
  clientName: string;
  coachId: string;
  open: boolean;
  onToggle: () => void;
  onDuplicated: (newWeekId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [unpublishing, setUnpublishing] = useState(false);

  /**
   * Publishing sets `status` and `published_at` in one statement, and the
   * table's own check constraint refuses any row where one is set without the
   * other — so "published with no date" is not a state this application has to
   * remember to avoid.
   */
  const publish = useMutation({
    mutationFn: () => publishWeek(supabase, week.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['weeks', clientId] });
    },
  });

  /**
   * Duplicating is offered on a published week as readily as on a draft — the
   * whole point is progressing last week into next week — and the copy is always
   * a draft the client cannot see, whatever the source was. That rule lives in
   * the database, not here, so this page cannot get it wrong.
   */
  const duplicate = useMutation({
    mutationFn: () => duplicateWeek(supabase, week.id),
    onSuccess: async (newWeekId) => {
      await queryClient.invalidateQueries({ queryKey: ['weeks', clientId] });
      onDuplicated(newWeekId);
    },
  });

  const published = week.status === 'published';

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={onToggle} className="flex items-center gap-3 text-left">
          <span className="text-ink font-medium">Week {week.week_number}</span>
          {published ? (
            <Badge tone="training">Published</Badge>
          ) : (
            <Badge tone="attention">Draft</Badge>
          )}
          {week.starts_on === null ? null : (
            <span className="text-ink-muted text-xs">from {formatDate(week.starts_on)}</span>
          )}
        </button>

        <div className="flex items-center gap-2">
          {published ? (
            /*
             * Not red, in any weight. Nothing is destroyed and she can publish
             * again, and the design-system spec draws the line exactly there: a
             * control whose effect she can undo without losing anything she
             * wrote is not a destructive control. The consequence to the client
             * is real, and it is carried by the confirmation's words, which is
             * where it belongs (design D7).
             */
            <Button
              variant="ghost"
              onClick={() => {
                setUnpublishing(true);
              }}
            >
              Unpublish week
            </Button>
          ) : (
            <Button
              disabled={publish.isPending}
              onClick={() => {
                publish.mutate();
              }}
            >
              {publish.isPending ? 'Publishing…' : 'Publish week'}
            </Button>
          )}
          <Button
            variant="ghost"
            disabled={duplicate.isPending}
            onClick={() => {
              duplicate.mutate();
            }}
          >
            {duplicate.isPending ? 'Duplicating…' : 'Duplicate week'}
          </Button>
          <Button variant="ghost" onClick={onToggle}>
            {open ? 'Hide' : 'Open'}
          </Button>
        </div>
      </div>

      {/*
        One sentence for the week, not one per session. Every disabled removal
        control inside this card points at it with `aria-describedby`, so a
        screen reader reaching any one of six unavailable controls is given the
        reason once — and without hovering, which a tooltip would have required
        (design D6).
      */}
      {published ? <PublishedWeekNotice weekId={week.id} clientName={clientName} /> : null}

      {unpublishing ? (
        <UnpublishDialog
          week={week}
          clientId={clientId}
          clientName={clientName}
          onClose={() => {
            setUnpublishing(false);
          }}
        />
      ) : null}

      {publish.isError ? (
        <p role="alert" className="text-danger mt-3 text-sm">
          {publish.error.message}
        </p>
      ) : null}

      {duplicate.isError ? (
        <p role="alert" className="text-danger mt-3 text-sm">
          {duplicate.error.message}
        </p>
      ) : null}

      {open ? (
        <div className="border-border mt-5 border-t pt-5">
          <SessionEditor week={week} clientId={clientId} coachId={coachId} />
        </div>
      ) : null}
    </Card>
  );
}

/**
 * Unpublishing a week, and why it ships alongside a rule nobody asked for it
 * with.
 *
 * "You may not delete a published session" with no way to unpublish is a dead
 * end: a coach who published a week with a wrong session in it holds a week she
 * cannot fix and a refusal with no ending. And with session editing absent from
 * the product entirely — there is no `updateSession` anywhere — a published week
 * would otherwise be append-only forever. This is the missing inverse of a gate
 * the product already had (design D7).
 *
 * It needs no migration. `grant update (week_number, starts_on, status,
 * published_at) on public.week` has been in place since `00005:155`, and the
 * table's own check constraint refuses any row where `status` and `published_at`
 * disagree — so "draft with a publication date" is not a state to remember to
 * avoid.
 *
 * The confirm button is `primary`, not `danger`: the dialog's words carry the
 * consequence to the client, and the colour carries whether anything is
 * destroyed. Nothing is.
 */
function UnpublishDialog({
  week,
  clientId,
  clientName,
  onClose,
}: {
  week: WeekRow;
  clientId: string;
  clientName: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const unpublish = useMutation({
    mutationFn: () => unpublishWeek(supabase, week.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['weeks', clientId] });
      onClose();
    },
  });

  return (
    <Dialog open onClose={onClose} title={`Unpublish week ${String(week.week_number)}?`}>
      <p className="text-ink-soft mt-3 text-sm leading-relaxed">
        {clientName} will stop seeing this week until you publish it again. Nothing in it is
        deleted, and you can then change or remove anything you like.
      </p>

      {unpublish.isError ? (
        <p role="alert" className="text-danger mt-4 text-sm">
          {unpublish.error.message}
        </p>
      ) : null}

      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Leave it published
        </Button>
        <Button
          disabled={unpublish.isPending}
          onClick={() => {
            unpublish.mutate();
          }}
        >
          {unpublish.isPending ? 'Unpublishing…' : 'Unpublish week'}
        </Button>
      </div>
    </Dialog>
  );
}
